import { parsePhoneNumberFromString } from "libphonenumber-js/min";
import { object, string } from "yup";

import { ConsentStatus } from "@/constants/contact.constants";
import { validateCNPJ, validateCPF } from "@/helpers/document-helpers";
import { buildFullPhone, buildPhoneProperties } from "@/helpers/phone-helpers";
import IContact, { IAddress, IAddressInput, IContactInput } from "@/interfaces/contact.interface";
import IUser from "@/interfaces/user.interface";
import { ContactAddressMessages } from "@/messages/contacts/address-section.messages";
import { ContactInfoMessages } from "@/messages/contacts/contact-Info.messages";
import { DocumentType, PersonType } from "@/types/document.types";
import { GenderType } from "@/types/gender.types";

import { AddressFormData, ContactFormData } from "./types";

type TagsType = string | string[] | undefined;

export const buildInitialData = (contact?: IContact | null): ContactFormData => {
  const parseTags = (tags: TagsType): string[] => {
    if (typeof tags === "string") {
      try {
        const parsedTags: string[] = JSON.parse(tags);
        return parsedTags.map((tag) => tag.replace(/^\[|\]$/g, ""));
      } catch (e) {
        return [];
      }
    }
    return (tags || []).map((tag) => tag.replace(/^\[|\]$/g, ""));
  };

  return {
    // Identifiers
    id: contact?.id || 0,
    customerId: contact?.customerId || 0,
    userId: contact?.userId || 0,

    // Person type
    persontype: contact?.persontype || PersonType.Individual,

    // Phone fields

    //MOBILE PHONE
    ddiMobile: contact?.ddiMobile || "",
    dddMobile: contact?.dddMobile || "",
    mobilePhone: contact?.mobilePhone || "",
    birthDate: contact?.birthDate || null,
    fullMobilePhone:
      contact?.ddiMobile || contact?.dddMobile || contact?.mobilePhone
        ? buildFullPhone(
            contact?.ddiMobile || "55",
            contact?.dddMobile || "",
            contact?.mobilePhone || ""
          )
        : "",

    //PHONE 1
    ddiPhone1: contact?.ddiPhone1 || "",
    dddPhone1: contact?.dddPhone1 || "",
    phone1: contact?.phone1 || "",
    fullPhone1:
      contact?.ddiPhone1 || contact?.dddPhone1 || contact?.phone1
        ? buildFullPhone(
            contact?.ddiPhone1 || "55",
            contact?.dddPhone1 || "",
            contact?.phone1 || ""
          )
        : "",

    //PHONE 2
    ddiPhone2: contact?.ddiPhone2 || "",
    dddPhone2: contact?.dddPhone2 || "",
    phone2: contact?.phone2 || "",
    fullPhone2:
      contact?.ddiPhone2 || contact?.dddPhone2 || contact?.phone2
        ? buildFullPhone(
            contact?.ddiPhone2 || "55",
            contact?.dddPhone2 || "",
            contact?.phone2 || ""
          )
        : "",

    fullNumber: contact?.fullNumber || "",

    // Document fields
    document: contact?.document || "",
    documentType: contact?.documentType || "",

    // Personal information
    firstName: contact?.firstName || "",
    lastName: contact?.lastName || "",
    nickName: contact?.nickName || "",
    email: contact?.email || "",
    gender: contact?.gender || GenderType.Unspecified,
    lastMessageDate: contact?.lastMessageDate || new Date(),
    tags: parseTags(contact?.tags),
    newTags: [],

    // Address fields
    addressName: contact?.addressName || "",
    addresses: contact?.addresses || [],
    address: contact?.address || "",
    addressNumber: contact?.addressNumber || "",
    addressCompl: contact?.addressCompl || "",
    addressDistrict: contact?.addressDistrict || "",
    addressCity: contact?.addressCity || "",
    addressState: contact?.addressState || "",
    addressStateUf: contact?.addressStateUf || "",
    addressZipCode: contact?.addressZipCode || "",
    addressCountry: contact?.addressCountry || "Brasil",

    consentStatus: contact?.consentStatus || ConsentStatus.NOT_COMPLETED,
    consentHistory: contact?.consentHistory || [],
    active: contact?.active ?? true,
    rowDate: contact?.rowDate,
    lastModifiedDate: contact?.lastModifiedDate ?? null,
    consentDate: contact?.consentDate ?? null,
  };
};

export const buildFormSchema = (messages: ContactInfoMessages) =>
  object().shape({
    firstName: string().required(messages.errorRequiredFirstName),

    fullMobilePhone: string().test("is-valid-phone", messages.errorInvalidPhone, (value) => {
      if (!value || value.trim() === "" || value === "55") return true; // Allow empty phone or just DDI
      const phoneNumber = parsePhoneNumberFromString(value, "BR");
      return phoneNumber ? phoneNumber.isValid() : false;
    }),

    document: string()
      .when("documentType", {
        is: DocumentType.CPF,
        then: (schema) =>
          schema.test("document", messages.invalidCPF, function (cpf?: string):
            | boolean
            | Promise<boolean> {
            return cpf ? validateCPF(cpf) : true; // Validate if CPF is provided, else pass
          }),
        otherwise: (schema) => schema, // No validation if documentType is not CPF
      })

      .when("documentType", {
        is: DocumentType.CNPJ,
        then: (schema) =>
          schema.test("document", messages.invalidCNPJ, function (cnpj?: string):
            | boolean
            | Promise<boolean> {
            return cnpj ? validateCNPJ(cnpj) : true; // Validate if CNPJ is provided, else pass
          }),
        otherwise: (schema) => schema, // No additional action if not CNPJ
      }),
    // Only firstName is required, other fields are optional but validated if provided
  });

export const normalizePhoneFormData = (formData: ContactFormData): IContactInput => {
  const {
    ddi: ddiMobile,
    ddd: dddMobile,
    phone: mobilePhone,
  } = buildPhoneProperties(formData.fullMobilePhone);
  const {
    ddi: ddiPhone1,
    ddd: dddPhone1,
    phone: phone1,
  } = buildPhoneProperties(formData.fullPhone1);
  const {
    ddi: ddiPhone2,
    ddd: dddPhone2,
    phone: phone2,
  } = buildPhoneProperties(formData.fullPhone2);

  return {
    ...formData,
    ddiMobile,
    dddMobile,
    mobilePhone,
    ddiPhone1,
    dddPhone1,
    phone1,
    ddiPhone2,
    dddPhone2,
    phone2,
  };
};

export const buildContactAddressFormSchema = (messages: ContactAddressMessages) => {
  return object().shape({
    addressName: string()
      .required(messages.errorRequiredAddressName)
      .max(20, messages.errorMaxAddressName),
    address: string().required(messages.errorRequiredAddress),
    addressNumber: string().required(messages.errorRequiredAddressNumber),
    addressCompl: string(),
    addressDistrict: string().required(messages.errorRequiredAddressDistrict),
    addressCity: string().required(messages.errorRequiredAddressCity),
    addressStateUf: string().required(messages.errorRequiredAddressStateUf),
    addressZipCode: string().required(messages.errorRequiredAddressZipCode),
    addressCountry: string().required(messages.errorRequiredAddressCountry),
  });
};
export const normalizeContactFormData = (
  _formData: ContactFormData,
  user: IUser
): IContactInput => {
  const formData = normalizePhoneFormData(_formData);

  let personType = PersonType.Individual;

  switch (formData.documentType) {
    case DocumentType.CPF:
      personType = PersonType.Individual;
      break;
    case DocumentType.CNPJ:
      personType = PersonType.LegalEntity;
      break;
    default:
      break;
  }

  const tagsArray = Array.isArray(formData.tags) ? formData.tags : [];
  const normalizedTags = tagsArray.map((tag) => `[${tag}]`);

  const normalizedFormData: IContactInput = {
    ...formData,
    userId: user.id,
    persontype: personType,
    tags: JSON.stringify(normalizedTags),
    active: _formData.active ?? true,
  };

  return normalizedFormData;
};

export const buildDefaultAddressFormData = (
  address: IAddress | null,
  contact: IContact | null
): AddressFormData => {
  return {
    addressName: address?.addressName || "",
    contactId: contact?.id || 0,
    address: address?.address || "",
    addressNumber: address?.addressNumber || "",
    addressCompl: address?.addressCompl || "",
    addressDistrict: address?.addressDistrict || "",
    addressCity: address?.addressCity || "",
    addressState: address?.addressState || "",
    addressStateUf: address?.addressStateUf || "",
    addressZipCode: address?.addressZipCode || "",
    addressCountry: address?.addressCountry || "Brasil",
  };
};

export const normalizeAddressFormData = (formData: AddressFormData): IAddressInput => {
  const normalizedFormData: IAddressInput = {
    ...formData,
    addressName: formData.addressName || "xxx",
  };

  return normalizedFormData;
};
